import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight, RotateCw } from 'lucide-react';
import { api } from '@/api/client';
import type { PastUsagesResp } from '@/api/types';
import { cn } from '@/lib/utils';
import { parseCollectionAllocation } from '@/lib/resource-helpers';
import DateRangeFilter, { defaultRange } from '@/components/resources/DateRangeFilter';
import DownloadDatasetButton from '@/components/resources/DownloadDatasetButton';
import TablePagination from '@/components/resources/TablePagination';

export default function PastUsagesPage() {
  const { name = '' } = useParams<{ name: string }>();
  const [range, setRange] = useState(() => defaultRange(7));
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['past-usages', name, range.start, range.end],
    queryFn: () =>
      api<PastUsagesResp>(
        `/v1/past-usages?resource-id=${encodeURIComponent(name)}` +
          `&start=${encodeURIComponent(range.start)}` +
          `&end=${encodeURIComponent(range.end)}`,
      ),
    enabled: name !== '',
    refetchOnWindowFocus: false,
  });

  const rows = data?.pastUsages ?? [];
  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const safePage = Math.min(page, totalPages - 1);
  const pageRows = useMemo(
    () => rows.slice(safePage * pageSize, (safePage + 1) * pageSize),
    [rows, safePage, pageSize],
  );

  useEffect(() => {
    setPage(0);
  }, [range.start, range.end, name]);

  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <div className="flex items-center justify-between gap-3">
          <nav
            aria-label="Breadcrumb"
            className="flex items-center gap-1.5 text-sm text-muted-foreground min-w-0"
          >
            <Link
              to="/resources"
              className="hover:text-foreground transition-colors shrink-0"
            >
              Resources
            </Link>
            <ChevronRight
              className="h-3.5 w-3.5 shrink-0 opacity-60"
              aria-hidden
            />
            <span className="text-foreground truncate">Past usages</span>
          </nav>
          <div className="flex items-center gap-2 shrink-0">
            <DownloadDatasetButton
              resourceName={name}
              start={range.start}
              end={range.end}
              downloadType="pastresourceusage"
              disabled={isFetching || name === ''}
            />
            <button
              onClick={() => refetch()}
              disabled={isFetching}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm border hover:bg-muted disabled:opacity-50 transition-colors shrink-0"
            >
              <RotateCw
                className={cn('h-4 w-4', isFetching && 'animate-spin')}
              />
              Refresh
            </button>
          </div>
        </div>
        <h1
          className="text-2xl font-semibold tracking-tight text-primary font-mono truncate"
          title={name}
        >
          {name}
        </h1>
      </div>

      <DateRangeFilter
        start={range.start}
        end={range.end}
        onApply={(start, end) => setRange({ start, end })}
        disabled={isFetching}
      />

      {isLoading && (
        <div className="py-12 text-center text-muted-foreground text-sm">
          Loading past usages…
        </div>
      )}

      {error && (
        <div className="py-3 px-4 rounded-md border border-destructive/30 bg-destructive/5 text-destructive text-sm">
          Failed to load past usages: {error instanceof Error ? error.message : String(error)}
        </div>
      )}

      {!isLoading && !error && (
        <div className="border rounded-lg overflow-hidden bg-card">
          <table className="w-full text-sm">
            <thead className="bg-muted/40">
              <tr className="text-left">
                <th className="px-3 py-2.5 font-medium">Allocated</th>
                <th className="px-3 py-2.5 font-medium">Deleted</th>
                <th className="px-3 py-2.5 font-medium">Duration</th>
                <th className="px-3 py-2.5 font-medium">Amount</th>
                <th className="px-3 py-2.5 font-medium">User</th>
                <th className="px-3 py-2.5 font-medium">Project</th>
                <th className="px-3 py-2.5 font-medium">Department</th>
                <th className="px-3 py-2.5 font-medium">Cluster</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td
                    colSpan={8}
                    className="py-16 text-center text-muted-foreground text-sm"
                  >
                    No past usages in this range.
                  </td>
                </tr>
              )}
              {pageRows.map((u, i) => {
                // Collection allocations carry which item indexes they held in
                // stringList["0"] (base64 JSON); other types either fold them
                // into `amount` or have none. See parseCollectionAllocation.
                const collectionItems =
                  u.resourceType === 'Collection'
                    ? parseCollectionAllocation(u.stringList?.[0])
                    : null;
                return (
                <tr
                  key={`${u.allocationDate}-${u.user}-${u.clusterId}-${i}`}
                  className="border-t"
                >
                  <td className="px-3 py-1.5 font-mono text-xs text-muted-foreground">
                    {formatDate(u.allocationDate)}
                  </td>
                  <td className="px-3 py-1.5 font-mono text-xs text-muted-foreground">
                    {formatDate(u.deleteTime)}
                  </td>
                  <td className="px-3 py-1.5 font-mono text-xs">
                    {u.duration || '—'}
                  </td>
                  <td className="px-3 py-1.5 font-mono">
                    {u.amount || '—'}
                    {collectionItems && collectionItems.length > 0 && (
                      <span
                        className="ml-1 text-muted-foreground"
                        title={collectionItems
                          .map((it) => `[${it.index}] ${it.value}`)
                          .join('\n')}
                      >
                        [{collectionItems.map((it) => it.index).join(', ')}]
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-1.5 font-medium">{u.user || '—'}</td>
                  <td className="px-3 py-1.5 text-muted-foreground">
                    {u.project || '—'}
                  </td>
                  <td className="px-3 py-1.5 text-muted-foreground">
                    {u.department || '—'}
                  </td>
                  <td className="px-3 py-1.5 font-mono text-xs text-muted-foreground break-all">
                    {u.clusterId || '—'}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
          <TablePagination
            page={safePage}
            pageSize={pageSize}
            totalRows={rows.length}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            rowLabel="record"
          />
        </div>
      )}
    </div>
  );
}

function formatDate(s: string): string {
  if (!s) return '—';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
